/**
 * Journey icon set — ported VERBATIM from the candidate-assessment-journey
 * prototype's inline SVGs (the design source of truth). Same 24×24 viewBox,
 * `currentColor` stroke and stroke widths, so every icon takes the text colour
 * of its context and is sized by the CSS that owns it (.btn svg, .check .ic svg,
 * …) — never give an icon its own hex or size here.
 * Design system: 03-foundations-layout-motion.md#iconography.
 */
type IconProps = { strokeWidth?: number };

function Glyph({
  strokeWidth = 2,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const MailIcon = () => (
  <Glyph>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </Glyph>
);

export const LockIcon = () => (
  <Glyph>
    <rect x="4" y="9" width="16" height="11" rx="2" />
    <path d="M8 9V6a4 4 0 0 1 8 0v3" />
  </Glyph>
);

export const ArrowRightIcon = () => (
  <Glyph strokeWidth={2.2}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Glyph>
);

export const ArrowLeftIcon = () => (
  <Glyph>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Glyph>
);

/** Info circle — demo hint row. */
export const InfoIcon = () => (
  <Glyph>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v.01M11 12h1v4h1" />
  </Glyph>
);

/** Alert circle — inline field errors and the error toast. */
export const AlertIcon = () => (
  <Glyph strokeWidth={2.2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </Glyph>
);

/** Clock — meta rows and the warning toast. */
export const ClockIcon = () => (
  <Glyph>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Glyph>
);

export const CheckIcon = ({ strokeWidth = 2.4 }: IconProps) => (
  <Glyph strokeWidth={strokeWidth}>
    <path d="M20 7 9 18l-5-5" />
  </Glyph>
);

/** Assessment document — the list card's icon tile. */
export const DocIcon = () => (
  <Glyph>
    <rect x="6" y="4" width="12" height="17" rx="2" />
    <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" />
    <path d="M9 12h6M9 16h4" />
  </Glyph>
);

export const SectionsIcon = () => (
  <Glyph>
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </Glyph>
);

export const AttemptIcon = () => (
  <Glyph>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 12 2 2 4-4" />
  </Glyph>
);

export const CameraIcon = () => (
  <Glyph>
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="m16 10 6-3v10l-6-3" />
  </Glyph>
);

export const MicIcon = () => (
  <Glyph>
    <path d="M12 3a4 4 0 0 0-4 4v3a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4Z" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </Glyph>
);

export const BrowserIcon = () => (
  <Glyph>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
  </Glyph>
);

export const NetworkIcon = () => (
  <Glyph>
    <path d="M5 12.5 9 9l4 5 6-7" />
    <path d="M3 20h18" />
  </Glyph>
);

export const FullscreenIcon = () => (
  <Glyph>
    <path d="M8 3H5a2 2 0 0 0-2 2v3" />
    <path d="M16 3h3a2 2 0 0 1 2 2v3" />
    <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
    <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
  </Glyph>
);

/** Crossed circle — a failed device check. */
export const FailIcon = () => (
  <Glyph strokeWidth={2.4}>
    <circle cx="12" cy="12" r="9" />
    <path d="m9.5 9.5 5 5m0-5-5 5" />
  </Glyph>
);

export const RetryIcon = () => (
  <Glyph>
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </Glyph>
);

/** Head-and-shoulders placeholder inside the camera tile. */
export const SilhouetteIcon = () => (
  <Glyph strokeWidth={1.6}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
  </Glyph>
);

export const FlagIcon = () => (
  <Glyph>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </Glyph>
);

export const LockIconSmall = () => (
  <Glyph>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Glyph>
);

export const PlayIcon = () => (
  <Glyph>
    <polygon points="6 3 20 12 6 21 6 3" />
  </Glyph>
);

/** Replaces the play glyph while a recording is playing back. */
export const PlayingIcon = () => (
  <Glyph strokeWidth={2.4}>
    <path d="M9 5v14M15 5v14" />
  </Glyph>
);

/** Opens the AI Interview options menu. */
export const MoreIcon = () => (
  <Glyph strokeWidth={2.6}>
    <path d="M12 5.5v.01M12 12v.01M12 18.5v.01" strokeLinecap="round" />
  </Glyph>
);

/* The four below are ported from the ai-interview prototype's resume step
   (pluginlive-designs/ai-interview/index.html, #stage-resume). */

/** Resume dropzone glyph. */
export const UploadCloudIcon = () => (
  <Glyph>
    <path d="M12 13v8M8 17l4 4 4-4" />
    <path d="M20 16.7A5 5 0 0 0 18 7h-1.3A8 8 0 1 0 4 15" />
  </Glyph>
);

/** Sits in the attached-file chip — the file is in, nothing is pending. */
export const FileCheckIcon = () => (
  <Glyph strokeWidth={2.4}>
    <path d="M20 7 9 18l-5-5" />
  </Glyph>
);

/** Removes the attached resume; also closes the field-level file chip. */
export const XIcon = () => (
  <Glyph>
    <path d="m6 6 12 12M18 6 6 18" />
  </Glyph>
);

/** Leads the "why we ask for this" note. */
export const SparkleIcon = () => (
  <Glyph>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
  </Glyph>
);

/** Accordion disclosure — rotates 180° open via the trigger's own CSS. */
export const ChevronDownIcon = () => (
  <Glyph strokeWidth={2.2}>
    <path d="m6 9 6 6 6-6" />
  </Glyph>
);

/** Drag handle — used alongside explicit move controls for reorder answers. */
export const GripIcon = () => (
  <Glyph>
    <circle cx="8" cy="6" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="16" cy="6" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="8" cy="12" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="16" cy="12" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="8" cy="18" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="16" cy="18" r="1.35" fill="currentColor" stroke="none" />
  </Glyph>
);

/** Assessment-type icons — semantic glyphs for assigned-test cards. */
export const AptitudeIcon = () => (
  <Glyph>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M8 9h3M9.5 7.5v3M14 9h3M15.5 7.5v3M8 15h3M14 14l3 3m0-3-3 3" />
  </Glyph>
);

export const CommunicationIcon = () => (
  <Glyph>
    <path d="M20 11a7 7 0 0 1-7 7H8l-4 3v-7a7 7 0 1 1 16-3Z" />
    <path d="M8 11h8M8 14h5" />
  </Glyph>
);

export const RoleBasedIcon = () => (
  <Glyph>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2" />
  </Glyph>
);

export const CustomIcon = () => (
  <Glyph>
    <path d="M5 7h14M5 12h14M5 17h14M8 5v4M16 10v4M11 15v4" />
  </Glyph>
);
